import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { hotelInfoService, hotelLogoSrc } from '../services/hotelInfoService';
import { getCurrentHotelCode, storeHotelCode } from '../utils/hotelContext';

/**
 * Shared hotel branding/profile data for every guest-facing screen, so the
 * app reads as the hotel's own app (logo + name everywhere) rather than a
 * generic guest portal.
 *
 * Resolves the hotel code from the room-QR context first, falling back to
 * the authenticated booking's hotel code. The fallback is persisted so
 * anonymous surfaces (e.g. /hotel-info after logout) keep working even for
 * guests who never arrived via a ?h= link. The query key matches the one
 * HotelInfoScreen historically used, so all screens share one cached fetch
 * of the public hotel-info endpoint.
 */
export function useHotelBranding(fallbackHotelCode?: string) {
  const { i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const contextCode = getCurrentHotelCode();
  const hotelCode = contextCode || fallbackHotelCode || null;

  useEffect(() => {
    if (!contextCode && fallbackHotelCode) {
      storeHotelCode(fallbackHotelCode);
    }
  }, [contextCode, fallbackHotelCode]);

  const query = useQuery({
    queryKey: ['hotelInfo', hotelCode, lang],
    queryFn: async () => {
      const response = await hotelInfoService.getHotelInfo(hotelCode as string, lang);
      return response.data;
    },
    enabled: !!hotelCode,
    // Hotel admins edit branding/features/labels live from the admin
    // panel — open guest sessions must pick changes up without a manual
    // reload. Focus-refetch covers "guest returns to the app"; the
    // interval covers kiosks and phones left open on a screen.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
    // Public, unauthenticated read for guests often on flaky hotel/captive
    // portal wifi where the browser's online/offline signal is unreliable.
    // React Query's default networkMode:'online' can pause a retry
    // indefinitely if that signal misreports offline, leaving screens stuck
    // on a spinner with no error surfaced. 'always' bypasses that gating —
    // genuine network failures still reject and surface via isError.
    networkMode: 'always' as const,
  });

  // Per-hotel colors (admin "Guest App" → Branding). Set on the document
  // root so every gd screen inherits them; the -soft variant gets a 12%
  // alpha of the accent for icon chips and ghost buttons, and the header
  // color drives the hero + dark cards.
  const brandColor = query.data?.hotel?.brand_color;
  const headerColor = query.data?.hotel?.header_color;
  useEffect(() => {
    const root = document.documentElement;
    const isHex = (value?: string) => !!value && /^#[0-9a-fA-F]{6}$/.test(value);
    if (isHex(brandColor)) {
      root.style.setProperty('--gd-blue', brandColor as string);
      root.style.setProperty('--gd-blue-soft', `${brandColor}1f`);
    } else {
      root.style.removeProperty('--gd-blue');
      root.style.removeProperty('--gd-blue-soft');
    }
    if (isHex(headerColor)) {
      root.style.setProperty('--gd-header', headerColor as string);
    } else {
      root.style.removeProperty('--gd-header');
    }
  }, [brandColor, headerColor]);

  return {
    hotelCode,
    hotelInfo: query.data,
    logoSrc: hotelLogoSrc(query.data?.hotel),
    query,
  };
}
